import React from 'react';
import { useState } from 'react';
import { X, Settings, Eye } from 'lucide-react';
import { FormField, FIELD_TYPES } from '../../types/formBuilder';
import { 
  BasicFieldSettings, 
  TypeSpecificSettings, 
  PromptBuilderSettings, 
  AdvancedFieldSettings, 
  FieldPreview 
} from './field-settings';

interface FieldSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  field: FormField | null;
  sectionId: string | null;
  onUpdate: (sectionId: string, fieldId: string, updates: Partial<FormField>) => void;
}

type SettingsTab = 'basic' | 'type-specific' | 'advanced' | 'preview';

export const FieldSettingsModal = ({ 
  isOpen, 
  onClose, 
  field, 
  sectionId, 
  onUpdate 
}: FieldSettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('basic');
  const [localField, setLocalField] = useState<FormField | null>(field);

  // Update local field state when prop changes
  React.useEffect(() => {
    setLocalField(field);
  }, [field]);

  const handleUpdate = (updates: Partial<FormField>) => {
    if (!localField || !sectionId) return;
    
    const updatedField = { ...localField, ...updates };
    setLocalField(updatedField);
    onUpdate(sectionId, localField.id, updates);
  };

  const handleClose = () => {
    setActiveTab('basic');
    onClose();
  };

  const fieldType = FIELD_TYPES.find(type => type.value === localField?.type);

  const tabs = [
    { id: 'basic', label: 'Basic', icon: Settings },
    { id: 'type-specific', label: 'Type Settings', icon: fieldType?.icon || Settings },
    { id: 'advanced', label: 'Advanced', icon: Settings },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  const hasTypeSpecificSettings = localField?.type && [
    'textarea', 'number', 'range', 'select', 'file', 'dropdown_with_image', 'prompt_builder'
  ].includes(localField.type);

  const visibleTabs = tabs.filter(tab => {
    if (tab.id === 'type-specific') {
      return hasTypeSpecificSettings;
    }
    return true;
  });

  if (!isOpen || !localField || !sectionId) return null;

  return (
    <div className="fixed inset-0 bg-base/90 z-modal-content flex items-center justify-center p-4">
      <div className="bg-surface border border-subtle rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-th-primary">Field Settings</h2>
              <p className="text-sm text-th-secondary">
                {localField.label || 'Unnamed Field'} ({fieldType?.label || localField.type})
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-subtle">
          <div className="flex items-center space-x-1 px-6 overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as SettingsTab)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-brand text-surface'
                    : 'text-th-secondary hover:text-th-primary hover:bg-overlay'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full flex">
            {/* Settings Panel */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-6 max-h-[calc(90vh-200px)]">
                {/* Field Type Selector - Always shown in basic tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-4 mb-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-th-primary">
                        Field Type *
                      </label>
                      <select
                        value={localField.type}
                        onChange={(e) => handleUpdate({ type: e.target.value as any })}
                        className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                      >
                        <option value="">Select Type</option>
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-th-secondary">
                        The type of input field to display
                      </p>
                    </div>
                  </div>
                )}

                {/* Tab Content */}
                <div className="space-y-6 pb-6">
                  {activeTab === 'basic' && (
                    <BasicFieldSettings field={localField} onUpdate={handleUpdate} />
                  )}

                  {activeTab === 'type-specific' && localField.type === 'prompt_builder' && (
                    <PromptBuilderSettings field={localField} onUpdate={handleUpdate} />
                  )}

                  {activeTab === 'type-specific' && localField.type !== 'prompt_builder' && (
                    <TypeSpecificSettings field={localField} onUpdate={handleUpdate} />
                  )}

                  {activeTab === 'advanced' && (
                    <AdvancedFieldSettings field={localField} onUpdate={handleUpdate} />
                  )}
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            {activeTab === 'preview' && (
              <div className="w-1/2 border-l border-subtle bg-surface overflow-y-auto">
                <FieldPreview field={localField} />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-subtle">
          <div className="text-sm text-th-secondary">
            Configure settings for this field
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary hover:border-brand/50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};